/**
 * pdfUtils.js — Diseño compartido para todos los PDFs de Moy Logistics.
 *
 *   import { pdfHeader, pdfFooter, pdfTableStyles, fmtMoney } from "../../../pdfUtils";
 *   (ajustar la ruta relativa según la profundidad del componente)
 *
 *   const doc = new jsPDF("landscape");
 *   const startY = pdfHeader(doc, "Listado de Abonos", `${fechaInicio} al ${fechaFin}`);
 *   doc.autoTable({ ...pdfTableStyles(), head, body, foot, startY });
 *   pdfFooter(doc);
 *   doc.save("Abonos.pdf");
 */

// ─── Paleta (misma que theme.css) ─────────────────────────────────────────────
const C = {
  dark:   [15, 23, 42],    // #0f172a — encabezado de tabla / títulos
  accent: [37, 99, 235],   // #2563eb — franja superior
  medium: [71, 85, 105],   // #475569 — subtítulos
  light:  [248, 250, 252], // #f8fafc — filas alternas
  border: [226, 232, 240], // #e2e8f0 — líneas
  foot:   [241, 245, 249], // #f1f5f9 — fila de totales
  white:  [255, 255, 255],
  muted:  [148, 163, 184], // #94a3b8 — pie de página
};

// Logo precargado al importar el módulo: jsPDF necesita la imagen ya descargada,
// si se crea y se usa en el mismo instante a veces sale en blanco.
const LOGO = typeof Image !== "undefined" ? new Image() : null;
if (LOGO) LOGO.src = `${process.env.PUBLIC_URL}/logoInstitucion.png`;

// ─── Formateadores ────────────────────────────────────────────────────────────
export const fmtMoney = (v, decimales = 2) =>
  "$" + new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(Number(v) || 0);

export const fmtMXN = (v) => fmtMoney(v, 0);

// ─── Encabezado ───────────────────────────────────────────────────────────────
/**
 * Dibuja el encabezado y regresa el startY sugerido para la tabla.
 * @param {jsPDF} doc
 * @param {string} title       Título, ej. "Listado de Abonos"
 * @param {string} [subtitle]  Subtítulo opcional, ej. "2026-01-01 al 2026-10-30"
 * @param {Object} [opciones]  { logo: false } para omitir el logo
 */
export function pdfHeader(doc, title, subtitle = "", opciones = {}) {
  const W = doc.internal.pageSize.getWidth();

  // Franja de color superior
  doc.setFillColor(...C.accent);
  doc.rect(0, 0, W, 3, "F");

  if (opciones.logo !== false && LOGO && LOGO.complete && LOGO.naturalWidth) {
    try {
      const alto = 22;
      const ancho = Math.min(50, (LOGO.naturalWidth / LOGO.naturalHeight) * alto);
      doc.addImage(LOGO, "PNG", 12, 8, ancho, alto);
    } catch (_) { /* sin logo, el resto del PDF sigue igual */ }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...C.dark);
  doc.text(title, W / 2, 17, { align: "center" });

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...C.medium);
    doc.text(String(subtitle), W / 2, 24, { align: "center" });
  }

  // Fecha de generación a la derecha
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...C.muted);
  doc.text(
    new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }),
    W - 12, 12, { align: "right" }
  );

  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.5);
  doc.line(12, 34, W - 12, 34);

  return 39;
}

// ─── Pie de página con paginación ─────────────────────────────────────────────
/** Llamar DESPUÉS de doc.autoTable y ANTES de doc.save(). */
export function pdfFooter(doc) {
  const total = doc.internal.getNumberOfPages();
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const nombre = process.env.REACT_APP_NOMBRE_INSTITUCION || "Moy Logistics";

  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(...C.border);
    doc.setLineWidth(0.3);
    doc.line(12, H - 12, W - 12, H - 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...C.muted);
    doc.text(nombre, 12, H - 7);
    doc.text("Powered by Lynqro", W / 2, H - 7, { align: "center" });
    doc.text(`Página ${i} de ${total}`, W - 12, H - 7, { align: "right" });
  }
}

// ─── Estilos de tabla ─────────────────────────────────────────────────────────
/** Base para doc.autoTable({ ...pdfTableStyles(), head, body, startY }). */
export function pdfTableStyles(extraHeadStyles = {}, extraBodyStyles = {}) {
  return {
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: { top: 2.6, right: 4, bottom: 2.6, left: 4 },
      font: "helvetica",
      textColor: C.dark,
      lineColor: C.border,
      lineWidth: 0.2,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: C.dark,
      textColor: C.white,
      fontStyle: "bold",
      fontSize: 7.8,
      halign: "center",
      cellPadding: { top: 3.5, right: 4, bottom: 3.5, left: 4 },
      ...extraHeadStyles,
    },
    alternateRowStyles: { fillColor: C.light },
    footStyles: {
      fillColor: C.foot,
      textColor: C.dark,
      fontStyle: "bold",
      fontSize: 8.2,
    },
    margin: { left: 12, right: 12, bottom: 16 },
    ...extraBodyStyles,
  };
}
